import React, { useEffect, useState, useRef } from "react";
import ReactQuill from "react-quill";
import { useDispatch, useSelector } from "react-redux";
import "react-quill/dist/quill.snow.css";
import "./Ticketing.css";
import DefaultButton from "../../../../shared/components/DefaultButton";
import { ErrorMessage, Field } from "formik";
import { SEND } from "../../../../shared/constants/Constants";
import { loadActiveEmployeeDataRequest } from "../../../employees/redux/Actions";
import { loadMessageTemplateDropdown } from "../redux/Actions";
import FormikSelectField from "../../../../shared/components/FormikSelectField";

const TicketSendMessage = ({ formik, ticketdetails, handleClose }) => {
  const dispatch = useDispatch();
  const { values, setFieldValue, handleSubmit } = formik;
  const { active_employees, loading, error  } = useSelector((store) => store.employee);
  const quillRef = useRef(null);
  const { templates } = useSelector((state) => state.messageTemplateDropdown);

  // ── Filter state ──────────────────────────────────────────────
  const [ccFilter, setCcFilter] = useState("All");

  // ── Handle filter button click ────────────────────────────────
    const handleFilterChange = (filter) => {
      setCcFilter(filter);
      setFieldValue("ccFilter", filter === "All" ? "" : filter);

      if (filter === "District") {
        dispatch(loadActiveEmployeeDataRequest({ filter_type: "district", filter_value: ticketdetails?.market_id }));
      } else if (filter === "SAP") {
        dispatch(loadActiveEmployeeDataRequest({ filter_type: "store", filter_value: ticketdetails?.store_id }));
      } else {
        // All — reload without params
        dispatch(loadActiveEmployeeDataRequest());
      }
    };

  // ── Initial loads ─────────────────────────────────────────────
  useEffect(() => {
    dispatch(loadActiveEmployeeDataRequest());
    dispatch(loadMessageTemplateDropdown("message"));
  }, [dispatch]);

  // ── Quill config ──────────────────────────────────────────────
  const modules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["bold", "italic", "underline", "strike"],
      ["link", "image"],
    ],
  };

  const handlePaste = (e) => {
    const clipboard = e.clipboardData;
    if (!clipboard) return;
    const items = clipboard.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf("image") !== -1) {
        const file = item.getAsFile();
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result;
          const quill = quillRef.current.getEditor();
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, "image", base64);
          quill.setSelection(range.index + 1);
        };
        reader.readAsDataURL(file);
        e.preventDefault();
      }
    }
  };

  return (
    <>
      <div className="send-message-container p-2">

        {/* Method */}
        <div className="mb-2">
          <label className="form-label">
            <strong>Method</strong>
          </label>
          <select
            name="method"
            className="form-select form-select-sm"
            value={values.method}
            onChange={formik.handleChange}
          >
            <option value="">Select Method</option> 
            <option value="email">Email</option>
            <option value="push">Push</option>
            <option value="both">Both</option>
          </select>
          <ErrorMessage name="method" component="div" className="text-danger small" />
        </div>

        {/* From + To */}
        <div className="d-flex gap-2 mb-2">
          <div style={{ flex: 1 }}>
            <label className="form-label"><strong>From</strong></label>
            <input
              name="from_emp_id"
              type="text"
              className="form-control"
              value={values.from_emp_id}
              onChange={formik.handleChange}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label className="form-label"><strong>To</strong></label>
            <input
              name="to_emp_id"
              type="text"
              className="form-control"
              value={values.to_emp_id}
              onChange={formik.handleChange}
            />
          </div>
        </div>

        {/* CC + Filter */}
        <div className="mb-2">
          <div className="d-flex align-items-center gap-2 mb-1">
            <label className="form-label mb-0"><strong>CC</strong></label>
            <div className="d-flex align-items-center gap-2 ms-auto">
              <span style={{ color: "#adb5bd", fontSize: "12px" }}>Filter by:</span>
              {["All", "District", "SAP"].map((filter) => (
                <span
                  key={filter}
                  style={{
                    padding: "2px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    cursor: loading ? "not-allowed" : "pointer",
                    border: "1.5px solid #adb5bd",
                    background: ccFilter === filter ? "#6c757d" : "white",
                    color: ccFilter === filter ? "white" : "#6c757d",
                    fontWeight: "500",
                    transition: "all 0.2s",
                    opacity: loading && ccFilter !== filter ? 0.5 : 1,
                  }}
                  onClick={() => !loading && handleFilterChange(filter)}
                >
                  {loading && ccFilter === filter ? (
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      style={{ width: "10px", height: "10px" }}
                    />
                  ) : null}
                  {filter}
                </span>
              ))}
            </div>
          </div>
          {error && ccFilter !== "All" && (
            <div className="d-flex align-items-center gap-1 mb-1" style={{ fontSize: "12px", color: "#dc3545" }}>
              <i className="fas fa-exclamation-circle" />
              <span>{ccFilter} filter unavailable, showing all employees</span>
            </div>
          )}

          <FormikSelectField
            name="cc"
            value={values.cc || []}
            isMulti={true}
            placeholder={
              loading
                ? "Loading employees..."
                : `Search and select employees... ${
                    ccFilter !== "All" ? `(${active_employees.length} found)` : ""
                  }`
            }
            onChange={(name, val) => setFieldValue(name, val)}
          >
            {active_employees.map((emp) => (
              <option key={emp.employee_id} value={emp.id}>
                {emp.first_name} {emp.last_name}, {emp.employee_id}
              </option>
            ))}
          </FormikSelectField>
        </div>

        {/* Subject + Template */}
        <div className="d-flex gap-2 align-items-end mb-3">
          <div style={{ flex: 1 }}>
            <label className="form-label"><strong>Subject</strong></label>
            <input
              name="subject"
              type="text"
              className="form-control"
              value={values.subject}
              onChange={formik.handleChange}
            />
            <ErrorMessage name="subject" component="div" className="text-danger small" />
          </div>
          <div style={{ width: "220px" }}>
            <Field
              as="select"
              name="selectedTemplateId"
              className="form-select form-select-sm"
              onChange={(e) => {
                const value = e.target.value;
                setFieldValue("selectedTemplateId", value);
                if (!value) { setFieldValue("body", ""); return; }
                const selectedTemplate = templates.find((t) => t.id === Number(value));
                if (selectedTemplate) setFieldValue("body", selectedTemplate.template_message);
              }}
            >
              <option value="">Select Template</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>{template.template_name}</option>
              ))}
            </Field>
          </div>
        </div>

        <div style={{ clear: "right" }} />

        {/* Quill Editor */}
        <div className="mb-3">
          <label><strong>Message</strong></label>
          <div className="ticket-quill-wrapper">
            <ReactQuill
              ref={quillRef}
              value={values.body}
              onChange={(val) => setFieldValue("body", val)}
              modules={modules}
              onPaste={handlePaste}
            />
          </div>
          <ErrorMessage name="body" component="div" className="text-danger small mt-1" />
        </div>

        {/* Attachments */}
        <div className="mb-3">
          <div className="d-flex align-items-center gap-3">
            <input
              type="file"
              id="fileUpload"
              multiple
              accept="image/gif,image/png,image/jpeg,image/jpg,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
              style={{ display: "none" }}
              onChange={(e) => {
                const newFiles = Array.from(e.target.files);
                const updated = values.attachments
                  ? [...values.attachments, ...newFiles]
                  : newFiles;
                setFieldValue("attachments", updated);
              }}
            />
            <button
              type="button"
              className="btn btn-light border"
              onClick={() => document.getElementById("fileUpload").click()}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <i className="fas fa-paperclip"></i> Attach
            </button>
          </div>

          {values.attachments && values.attachments.length > 0 && (
            <div className="mt-2 d-flex flex-wrap" style={{ gap: "12px" }}>
              {values.attachments.map((file, index) => {
                const isGif = file.type === "image/gif";
                const isImage = file.type.startsWith("image/");
                const previewUrl = isImage ? URL.createObjectURL(file) : null;
                return (
                  <div
                    key={index}
                    className="d-flex flex-column align-items-center border rounded p-2"
                    style={{
                      gap: "6px",
                      fontSize: "14px",
                      background: "#f8f9fa",
                      minWidth: isGif ? "140px" : "200px",
                      maxWidth: "200px",
                    }}
                  >
                    {isImage && previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={file.name}
                        style={{ width: "100%", maxHeight: "120px", objectFit: "contain", borderRadius: "4px" }}
                        onLoad={(e) => { if (!isGif) URL.revokeObjectURL(e.target.src); }}
                      />
                    ) : (
                      <i className="far fa-file fa-2x text-muted" />
                    )}
                    <span style={{ maxWidth: "130px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: "12px" }}>
                      {file.name}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => {
                        if (previewUrl) URL.revokeObjectURL(previewUrl);
                        const updated = values.attachments.filter((_, i) => i !== index);
                        setFieldValue("attachments", updated);
                      }}
                      style={{ padding: "2px 8px", width: "100%" }}
                    >
                      <i className="fa fa-trash" /> Remove
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="d-flex justify-content-end mt-3 gap-2">
          <DefaultButton
            buttonClass="btn btn-secondary"
            buttonTitle="Cancel"
            buttonType="button"
            actionBlock={handleClose}
          />
          <DefaultButton
            buttonClass="default-btn-color"
            buttonTitle={SEND}
            buttonType="button"
            actionBlock={handleSubmit}
          />
        </div>
      </div>
    </>
  );
};

export default TicketSendMessage;